import React, { useEffect, useRef } from 'react'
import type { VideoScreen as VideoScreenType } from '@mission.io/mission-toolkit'
import { useSilentMissionPhaseRef } from '../../../sound/hooks'
import { usePrevious, type MediaRef } from '../../../utility'
import {
	getIsScreenLooping,
	useMissionStateSelector,
	useResolvers,
	useOnVideoScreenEnd,
} from '../../../context'
import { ConnectedVideo } from '../../basics'

type Props = {
	showASLVideo: boolean,
	screen: VideoScreenType,
}

export default function VideoScreen({
	showASLVideo,
	screen,
}: Props): React$Node {
	const videoRef: { current: HTMLVideoElement | null } = useRef(null)
	const { videoSourceResolver } = useResolvers()
	const onVideoScreenEnd = useOnVideoScreenEnd()
	const isScreenLooping = useMissionStateSelector(getIsScreenLooping)

	const previousScreen = usePrevious(screen)

	useEffect(() => {
		if (
			videoRef.current &&
			screen.type === 'VIDEO' &&
			previousScreen &&
			previousScreen.type === 'VIDEO' &&
			screen.url === previousScreen.url &&
			screen.timestamp !== previousScreen.timestamp
		) {
			videoRef.current.currentTime = 0
			videoRef.current.play() // Note: if this should be paused due to the missionState, it will be paused instantly by an event handler added by `useMissionPauseForRef` in `ConnectedVideo`
		}
	}, [screen, previousScreen])

	const videoUrl: string = videoSourceResolver(screen.url)

	const previousVideoUrl = useRef(videoUrl)
	useEffect(() => {
		if (previousVideoUrl.current === videoUrl) {
			return
		}

		if (videoRef.current) {
			videoRef.current.load()
		}
		previousVideoUrl.current = videoUrl
	}, [videoUrl])

	useSilentMissionPhaseRef({ ref: ((videoRef: any): MediaRef) })

	const extraProps: { loop?: boolean, onEnded?: () => void } = {}
	if (isScreenLooping) {
		extraProps.loop = true
	} else {
		extraProps.onEnded = () => {
			const sendEndVideoScreen = () => {
				onVideoScreenEnd(screen._actionId)
			}
			if (!screen.finalFrameDuration) sendEndVideoScreen()
		}
	}

	return <ConnectedVideo {...extraProps} url={videoUrl} ref={videoRef} />
}
